/* eslint-disable react/forbid-prop-types */
import { useCallback, useEffect, useState } from "react";
import "react-image-crop/dist/ReactCrop.css";
import Modal from "common/components/Modal/Modal";
import ReactCrop from "react-image-crop";
import { useTranslation } from "react-i18next";

export type CropModalProps = {
  tmpFiles: Array<any>;
  setTmpFiles: (value: any) => void;
  confirmModal: (value: any) => void;
  closeModal: () => void;
  visible?: boolean;
  maxWidth?: number;
  maxHeight?: number;
};

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
}

function CropModal({
  tmpFiles,
  setTmpFiles,
  confirmModal,
  maxWidth = 1600,
  maxHeight = 1200,
  closeModal,
  visible = true,
}: CropModalProps) {
  const { t } = useTranslation();
  const [canvasUrls, setCanvasUrls] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const generateCanvasImage = useCallback(
    async (url: string, aspect: number): Promise<string> => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Failed to get canvas context");

      const image = await loadImage(url);

      canvas.width = maxWidth;
      canvas.height = maxHeight;

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const scale = Math.min(
        canvas.width / image.width,
        canvas.height / image.height
      );
      const newWidth = image.width * scale;
      const newHeight = image.height * scale;
      const offsetX = (canvas.width - newWidth) / 2;
      const offsetY = (canvas.height - newHeight) / 2;

      ctx.drawImage(image, offsetX, offsetY, newWidth, newHeight);

      return canvas.toDataURL("image/png");
    },
    [maxWidth, maxHeight]
  );

  useEffect(() => {
    const initCanvas = async () => {
      if (visible && tmpFiles.length > 0 && canvasUrls.length === 0) {
        const results = await Promise.all(
          tmpFiles.map(async (file) => {
            const canvasUrl = await generateCanvasImage(
              file.url,
              file.crop.aspect
            );
            // Pre-charger l'image pour stocker la ref
            const img = await loadImage(canvasUrl);
            return { canvasUrl, img };
          })
        );

        setCanvasUrls(results.map((r) => r.canvasUrl));
        setCurrentIndex(0);

        // Stocker les refs image et initialiser le crop en % pour tous les fichiers
        setTmpFiles((prev: any[]) =>
          prev.map((file, i) => ({
            ...file,
            image: results[i].img,
            crop: {
              ...file.crop,
              unit: "%" as const,
              x: 0,
              y: 0,
              width: 100,
              height: 100,
            },
          }))
        );
      }
    };
    initCanvas();
  }, [
    visible,
    tmpFiles.length,
    canvasUrls.length,
    tmpFiles,
    setTmpFiles,
    generateCanvasImage,
  ]);

  const handleCropChange = useCallback(
    (_pixelCrop: any, percentCrop: any) => {
      setTmpFiles((prev: any[]) =>
        prev.map((file, i) =>
          i === currentIndex
            ? { ...file, crop: { ...percentCrop, aspect: file.crop.aspect } }
            : file
        )
      );
    },
    [setTmpFiles, currentIndex]
  );

  const handleConfirm = () => {
    setCanvasUrls([]);
    setCurrentIndex(0);
    confirmModal(tmpFiles);
  };

  const handleClose = () => {
    setCanvasUrls([]);
    setCurrentIndex(0);
    closeModal();
  };

  const isMultiple = canvasUrls.length > 1;
  const currentFile = tmpFiles[currentIndex];
  const currentCanvasUrl = canvasUrls[currentIndex];

  return (
    <Modal
      onConfirmModal={handleConfirm}
      onCloseModal={handleClose}
      visible={visible}
    >
      <div
        style={{
          width: "500px",
          padding: 10,
        }}
      >
        {isMultiple && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <button
              type="button"
              onClick={() => setCurrentIndex((i) => i - 1)}
              disabled={currentIndex === 0}
              style={{
                padding: "4px 12px",
                cursor: currentIndex === 0 ? "not-allowed" : "pointer",
                opacity: currentIndex === 0 ? 0.4 : 1,
              }}
            >
              &larr; {t("common.previous")}
            </button>
            <span>
              {currentIndex + 1} / {canvasUrls.length}
            </span>
            <button
              type="button"
              onClick={() => setCurrentIndex((i) => i + 1)}
              disabled={currentIndex === canvasUrls.length - 1}
              style={{
                padding: "4px 12px",
                cursor:
                  currentIndex === canvasUrls.length - 1
                    ? "not-allowed"
                    : "pointer",
                opacity: currentIndex === canvasUrls.length - 1 ? 0.4 : 1,
              }}
            >
              {t("common.next")} &rarr;
            </button>
          </div>
        )}
        {currentCanvasUrl && currentFile && (
          <ReactCrop
            crop={currentFile.crop}
            onChange={handleCropChange}
            aspect={currentFile.crop?.aspect}
          >
            <img key={currentIndex} alt="crop" src={currentCanvasUrl} />
          </ReactCrop>
        )}
      </div>
    </Modal>
  );
}

export default CropModal;
